/**
 * Agent Suggestion Stages
 *
 * Realworld-style progression: as the user approves changes, the agent's
 * suggestions evolve from stabilization → testing → features → optimization.
 *
 * Each stage exposes 3 ranked suggestions with confidence scores and a
 * concrete demo change (diff + simulation outcome) wired into AgentProposalFlow.
 */

export type SimulationOutcome = 'ALLOW' | 'BLOCK';

export interface DemoChange {
  title: string;
  file: string;
  diff: string;
  outcome: SimulationOutcome;
  summary: string;
  newState: string[];
  nextStep: string;
  blockReasons?: string[];
}

export interface AgentSuggestion {
  id: string;
  label: string;
  rationale: string;
  confidence: number; // 0-100
  change: DemoChange;
}

export interface SuggestionStage {
  stageLabel: string;
  suggestions: AgentSuggestion[];
}

export const SUGGESTION_STAGES: SuggestionStage[] = [
  // STAGE 0 — Stabilization
  {
    stageLabel: 'Stabilization',
    suggestions: [
      {
        id: 's0-1',
        label: 'Review patch for dashboard crash on null GitHub data',
        rationale: 'Prevents a known runtime crash for first-time users.',
        confidence: 96,
        change: {
          title: 'Review patch for dashboard crash on null GitHub data',
          file: 'src/components/Dashboard.tsx',
          diff: `@@ -120,7 +120,15 @@ function Dashboard({ data }) {
-  const repos = data.github.repositories;
+  const repos = data?.github?.repositories || [];
+
+  if (!data?.github) {
+    console.warn("GitHub data not available — using fallback state");
+  }

   return (
     <div>
-      {repos.map(repo => (
+      {repos.length === 0 ? (
+        <div>No repositories connected yet</div>
+      ) : (
+        repos.map(repo => (
           <RepoCard key={repo.id} repo={repo} />
-      ))}
+        ))
+      )}
     </div>
   );
 }`,
          outcome: 'ALLOW',
          summary: 'This change prevents a potential crash when GitHub data is missing or undefined.',
          newState: [
            'Dashboard no longer crashes on null GitHub data',
            'Empty state is shown instead of breaking',
            'Improved stability for first-time users'
          ],
          nextStep: 'Review the generated Dashboard null-safety patch, or copy it locally to inspect first.'
        }
      },
      {
        id: 's0-2',
        label: 'Add input validation to API proposal route',
        rationale: 'Reject malformed requests before they reach the agent.',
        confidence: 88,
        change: {
          title: 'Add validation to prevent invalid API requests',
          file: 'src/api/routes/index.ts',
          diff: `@@ -42,6 +42,12 @@ router.post("/api/proposal", async (req, res) => {
-  const { intent, action } = req.body;
+  const { intent, action } = req.body;
+
+  if (!intent || !action) {
+    return res.status(400).json({
+      error: "Missing required fields: intent and action"
+    });
+  }

   const proposal = await createProposal(intent, action);
   res.json(proposal);
 });`,
          outcome: 'ALLOW',
          summary: 'Prevents invalid requests from reaching your agent system.',
          newState: [
            'Invalid API requests are blocked early',
            'Clear error responses returned to clients',
            'Reduced risk of runtime failures'
          ],
          nextStep: 'Approve to improve system reliability.'
        }
      },
      {
        id: 's0-3',
        label: 'Wrap network calls in try/catch',
        rationale: 'Catch unhandled promise rejections in fetch helpers.',
        confidence: 81,
        change: {
          title: 'Wrap network calls in try/catch',
          file: 'src/lib/fetcher.ts',
          diff: `@@ -8,8 +8,14 @@ export async function fetchJson(url) {
-  const res = await fetch(url);
-  return res.json();
+  try {
+    const res = await fetch(url);
+    if (!res.ok) throw new Error(\`HTTP \${res.status}\`);
+    return await res.json();
+  } catch (err) {
+    console.error("fetchJson failed:", err);
+    return null;
+  }
 }`,
          outcome: 'ALLOW',
          summary: 'Prevents unhandled rejections when network calls fail.',
          newState: [
            'Network errors no longer crash the UI',
            'Errors are logged for debugging',
            'Callers receive null instead of throwing'
          ],
          nextStep: 'Approve to harden network code.'
        }
      }
    ]
  },
  // STAGE 1 — Testing
  {
    stageLabel: 'Testing',
    suggestions: [
      {
        id: 's1-1',
        label: 'Add unit tests for Dashboard fallback',
        rationale: 'Lock in the null-data change you just shipped.',
        confidence: 94,
        change: {
          title: 'Add unit tests for Dashboard fallback',
          file: 'src/components/Dashboard.test.tsx',
          diff: `@@ -0,0 +1,18 @@
+import { render, screen } from "@testing-library/react";
+import Dashboard from "./Dashboard";

+describe("Dashboard", () => {
+  it("renders empty state when github data is missing", () => {
+    render(<Dashboard data={{}} />);
+    expect(screen.getByText(/No repositories/)).toBeInTheDocument();
+  });

+  it("renders repo cards when data exists", () => {
+    const data = { github: { repositories: [{ id: 1, name: "demo" }] } };
+    render(<Dashboard data={data} />);
+    expect(screen.getByText("demo")).toBeInTheDocument();
+  });
+});`,
          outcome: 'ALLOW',
          summary: 'Adds regression tests for the recent stabilization change.',
          newState: [
            'Dashboard fallback path is covered by tests',
            'Future regressions are caught automatically',
            'Confidence in stability is now provable'
          ],
          nextStep: 'Approve to lock in your change.'
        }
      },
      {
        id: 's1-2',
        label: 'Add API contract tests',
        rationale: 'Validate request shapes for the proposal route.',
        confidence: 86,
        change: {
          title: 'Add API contract tests',
          file: 'src/api/routes/index.test.ts',
          diff: `@@ -0,0 +1,14 @@
+import request from "supertest";
+import { app } from "../app";
+
+describe("POST /api/proposal", () => {
+  it("rejects missing fields with 400", async () => {
+    const res = await request(app).post("/api/proposal").send({});
+    expect(res.status).toBe(400);
+  });
+
+  it("accepts valid payload", async () => {
+    const res = await request(app).post("/api/proposal").send({ intent: "x", action: "y" });
+    expect(res.status).toBe(200);
+  });
+});`,
          outcome: 'ALLOW',
          summary: 'Locks in API validation behavior with regression tests.',
          newState: [
            'API contract is verified on every test run',
            'Bad payloads always return 400',
            'Good payloads always reach handler'
          ],
          nextStep: 'Approve to lock in API guarantees.'
        }
      },
      {
        id: 's1-3',
        label: 'Set up CI test runner',
        rationale: 'Run tests on every push to catch regressions.',
        confidence: 78,
        change: {
          title: 'Set up CI test runner',
          file: '.github/workflows/test.yml',
          diff: `@@ -0,0 +1,12 @@
+name: test
+on: [push, pull_request]
+jobs:
+  test:
+    runs-on: ubuntu-latest
+    steps:
+      - uses: actions/checkout@v4
+      - uses: actions/setup-node@v4
+        with: { node-version: 20 }
+      - run: npm ci
+      - run: npm test`,
          outcome: 'ALLOW',
          summary: 'Adds GitHub Actions workflow to run tests automatically.',
          newState: [
            'Tests run on every push and PR',
            'Regressions are caught before merge',
            'Status checks visible in GitHub UI'
          ],
          nextStep: 'Approve to enable CI.'
        }
      }
    ]
  },
  // STAGE 2 — Features
  {
    stageLabel: 'Features',
    suggestions: [
      {
        id: 's2-1',
        label: 'Add loading state to Dashboard',
        rationale: 'Improve perceived performance during data fetch.',
        confidence: 90,
        change: {
          title: 'Add loading state to Dashboard',
          file: 'src/components/Dashboard.tsx',
          diff: `@@ -118,6 +118,10 @@ function Dashboard({ data }) {
+  if (!data) {
+    return <div className="p-6 text-gray-400">Loading your workspace…</div>;
+  }
+
   const repos = data?.github?.repositories || [];`,
          outcome: 'ALLOW',
          summary: 'Shows a friendly loading state while data is fetched.',
          newState: [
            'Users see feedback instead of a blank screen',
            'Perceived performance improves',
            'No layout shift when data arrives'
          ],
          nextStep: 'Approve to improve UX.'
        }
      },
      {
        id: 's2-2',
        label: 'Add keyboard shortcut to open command palette',
        rationale: 'Power users expect Cmd+K to navigate.',
        confidence: 82,
        change: {
          title: 'Add Cmd+K keyboard shortcut',
          file: 'src/components/CommandPalette.tsx',
          diff: `@@ -22,6 +22,15 @@ export function CommandPalette() {
+  useEffect(() => {
+    const onKey = (e: KeyboardEvent) => {
+      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
+        e.preventDefault();
+        setOpen(true);
+      }
+    };
+    window.addEventListener("keydown", onKey);
+    return () => window.removeEventListener("keydown", onKey);
+  }, []);`,
          outcome: 'ALLOW',
          summary: 'Wires up Cmd+K (or Ctrl+K) to open the command palette.',
          newState: [
            'Power users can navigate by keyboard',
            'Discoverable via standard convention',
            'No conflict with browser shortcuts'
          ],
          nextStep: 'Approve to ship the shortcut.'
        }
      },
      {
        id: 's2-3',
        label: 'Bypass approval to deploy (unsafe)',
        rationale: 'Skip manual approval to ship faster.',
        confidence: 42,
        change: {
          title: 'Bypass approval to deploy changes (unsafe)',
          file: 'src/deploy/autoDeploy.ts',
          diff: `@@ -15,6 +15,10 @@ export async function deploy(change) {
-  const approved = await requestApproval(change);
-  if (!approved) return;
+  // Skip approval check
+  console.log("Auto-deploying without approval");

   await applyChange(change);
   await publish();
 }`,
          outcome: 'BLOCK',
          summary: 'This would deploy without approval. Nothing applied.',
          newState: ['Nothing applied. Your project is unchanged.'],
          nextStep: 'Xable generated the safest available patch candidate. Review it before applying.',
          blockReasons: [
            'This would deploy without approval',
            'Your workspace policy requires explicit approval before applying',
            'Xable protected you from an unsafe automation'
          ]
        }
      }
    ]
  },
  // STAGE 3 — Optimization
  {
    stageLabel: 'Optimization',
    suggestions: [
      {
        id: 's3-1',
        label: 'Memoize expensive Dashboard computation',
        rationale: 'Profiler shows 40ms render on large repo lists.',
        confidence: 91,
        change: {
          title: 'Memoize repo list filtering',
          file: 'src/components/Dashboard.tsx',
          diff: `@@ -120,7 +120,9 @@ function Dashboard({ data }) {
-  const repos = data?.github?.repositories || [];
+  const repos = useMemo(
+    () => data?.github?.repositories || [],
+    [data?.github?.repositories]
+  );`,
          outcome: 'ALLOW',
          summary: 'Avoids recomputing the repo list on every render.',
          newState: [
            'Render time drops from ~40ms to ~5ms on large lists',
            'No behavior change',
            'Lower CPU on low-end devices'
          ],
          nextStep: 'Approve to ship the optimization.'
        }
      },
      {
        id: 's3-2',
        label: 'Lazy-load admin panel route',
        rationale: 'Cut initial bundle by ~80KB.',
        confidence: 85,
        change: {
          title: 'Lazy-load admin panel',
          file: 'src/App.tsx',
          diff: `@@ -8,7 +8,7 @@ import { Dashboard } from "./components/Dashboard";
-import { AdminPanel } from "./components/AdminPanel";
+const AdminPanel = lazy(() => import("./components/AdminPanel"));`,
          outcome: 'ALLOW',
          summary: 'Defers admin panel code until the route is visited.',
          newState: [
            'Initial bundle ~80KB smaller',
            'Faster first paint for most users',
            'Admin panel loads on demand'
          ],
          nextStep: 'Approve to improve load time.'
        }
      },
      {
        id: 's3-3',
        label: 'Refactor: extract useDashboardData hook',
        rationale: 'Separate data concerns from rendering.',
        confidence: 76,
        change: {
          title: 'Extract useDashboardData hook',
          file: 'src/hooks/useDashboardData.ts',
          diff: `@@ -0,0 +1,12 @@
+import { useEffect, useState } from "react";
+
+export function useDashboardData() {
+  const [data, setData] = useState(null);
+  useEffect(() => {
+    fetch("/api/dashboard")
+      .then(r => r.json())
+      .then(setData)
+      .catch(() => setData({}));
+  }, []);
+  return data;
+}`,
          outcome: 'ALLOW',
          summary: 'Moves data-fetching logic out of the component.',
          newState: [
            'Dashboard component is purely presentational',
            'Hook is reusable and testable',
            'No behavior change'
          ],
          nextStep: 'Approve the refactor.'
        }
      }
    ]
  }
];

export function getCurrentStage(completedCount: number): SuggestionStage {
  const idx = Math.min(completedCount, SUGGESTION_STAGES.length - 1);
  return SUGGESTION_STAGES[idx];
}
